// Directional probes — LLM-generated + author free-text — Story #4937
// Stream 3c; Story #5305 Task #5326 Stream 11b (design §5.4 "Probes that
// can be trusted") wires in the two named actions, anchor-named numbers,
// the plausibility flag, and a real accept receipt.
//
// TWO NAMED FEATURES, not one blurred button (design §5.4): "Probe this
// region on the map" is THIS component's whole job — embed a candidate
// direction and place it on the corpus-anchored map, carrying whatever
// region context the caller passed in via `initialContext` (the caller
// re-mounts this component with a fresh `key` when that context changes,
// so a probe run from one region never silently reuses a stale hint from
// the last one). "Run novelty analysis" is a SEPARATE, already-existing
// feature (the project's AI-analysis pipeline) — this panel only offers a
// named link to it via `onRunNoveltyAnalysis`, it never runs it itself.
//
// Lives on the Landscape Map view: a probe's whole point is seeing where it
// lands relative to the corpus-anchored map
// (lambda/innovateflow-api/whitespace/probes.py), so its UI sits next to
// LandscapeMap rather than inside the (do-not-touch) OpportunityExplorer.
import { useState } from 'react'
import { Compass, Loader2, Zap, CheckCircle2 } from 'lucide-react'
import { acceptWhitespaceOpportunity, requestWhitespaceProbe } from '../../services/api'
import type { WhitespaceProbeLanding, WhitespaceProbeMode } from '../../types/whitespace'
import HelpPopover from '../help/HelpPopover'
import { floorToDisplayInteger } from '../../lib/whitespace'

interface ProbePanelProps {
  projectId: string
  /** Optional steering hint, e.g. pre-filled from a region the inventor
   * clicked "Probe this region on the map" on. The caller is expected to
   * remount this component (a fresh `key`) whenever the context changes —
   * see this file's module docstring. */
  initialContext?: string
  onLandings: (landings: WhitespaceProbeLanding[]) => void
  /** Names the destination of the SEPARATE novelty-analysis handoff (design
   * §5.4) — never invoked from inside this component, only linked to. */
  onRunNoveltyAnalysis?: () => void
}

/** design §5.1: "Numbers always name their anchor" (Bug #5299's own example
 * was this exact field printed bare as "0.42"). This is a Euclidean
 * distance in the map's own projected 2-D space, not a similarity — so it
 * gets its own honest ruler, never the 0-100 similarity scale. */
export function distanceRulerLabel(distance: number): string {
  return `Distance to the nearest point on this map: ${distance.toFixed(2)} (projected-space units, lower = closer)`
}

/** design §5.1's fixed "Closest existing record: NN" anchor phrasing for a
 * probe result — floor(100 × value), the same displayed-integer rule every
 * similarity number uses, never Math.round (which can print an integer one
 * band higher than the value actually earns). */
export function closestRecordAnchor(similarity: number): string {
  return `Closest existing record: ${floorToDisplayInteger(similarity)}`
}

/** What a single probe result card shows, worked out once so the run→
 * result→plausibility-flag→accept→receipt flow (Bug #5336) is unit-testable
 * without a network call — the labels themselves are pure text built from
 * whatever the backend actually returned, never fabricated here.
 * `noNearbyLabel` and the two number labels are mutually exclusive: a probe
 * always has either a resolvable distance/anchor reading or the honest
 * "nothing to compare against" message, never both. */
export interface ProbeLandingDisplay {
  distanceLabel?: string
  anchorLabel?: string
  noNearbyLabel?: string
  /** The backend's own plausibility-flag text, passed through verbatim —
   * this component never computes or second-guesses the threshold that
   * produced it. */
  plausibilityFlag?: string
  /** Set once this landing's text has been accepted and the backend
   * returned a receipt id for it. */
  receiptLabel?: string
}

export function probeLandingDisplay(landing: WhitespaceProbeLanding, receiptId?: string): ProbeLandingDisplay {
  const hasDistance = landing.nearest_neighbour_distance != null
  const hasAnchor = landing.closest_record_similarity != null
  return {
    distanceLabel: hasDistance ? distanceRulerLabel(landing.nearest_neighbour_distance!) : undefined,
    anchorLabel: hasAnchor ? closestRecordAnchor(landing.closest_record_similarity!) : undefined,
    noNearbyLabel: !hasDistance && !hasAnchor ? 'No nearby points to compare against yet.' : undefined,
    plausibilityFlag: landing.plausibility_flag || undefined,
    receiptLabel: receiptId ? `Saved — receipt ${receiptId}` : undefined,
  }
}

export default function ProbePanel({ projectId, initialContext = '', onLandings, onRunNoveltyAnalysis }: ProbePanelProps) {
  const [mode, setMode] = useState<WhitespaceProbeMode>('llm')
  const [contextHint, setContextHint] = useState(initialContext)
  const [freeText, setFreeText] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastProbes, setLastProbes] = useState<WhitespaceProbeLanding[]>([])
  const [acceptedTexts, setAcceptedTexts] = useState<Set<string>>(new Set())
  const [acceptReceipts, setAcceptReceipts] = useState<Record<string, string>>({})
  const [acceptingText, setAcceptingText] = useState<string | null>(null)
  const [acceptError, setAcceptError] = useState<string | null>(null)

  const runProbe = async () => {
    setError(null)
    setIsRunning(true)
    try {
      const body =
        mode === 'llm'
          ? { mode: 'llm' as const, context: contextHint, count: 3 }
          : { mode: 'freetext' as const, text: freeText }
      const result = await requestWhitespaceProbe(projectId, body)
      setLastProbes(result.probes)
      onLandings(result.probes)
      if (result.probes.length === 0) {
        setError('No directions were generated — try adding more context about your idea.')
      }
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to run probe.')
    } finally {
      setIsRunning(false)
    }
  }

  const acceptProbe = async (probe: WhitespaceProbeLanding) => {
    setAcceptError(null)
    setAcceptingText(probe.text)
    try {
      const result = await acceptWhitespaceOpportunity(projectId, {
        text: probe.text,
        position: probe.position ?? null,
        nearest_neighbour_distance: probe.nearest_neighbour_distance ?? null,
        closest_record_similarity: probe.closest_record_similarity ?? null,
        mode,
      })
      setAcceptedTexts((prev) => new Set(prev).add(probe.text))
      setAcceptReceipts((prev) => ({ ...prev, [probe.text]: result.analysis_run_id }))
    } catch (e) {
      setAcceptError((e as Error)?.message ?? 'Failed to save this probe — please try again.')
    } finally {
      setAcceptingText(null)
    }
  }

  const canRun = mode === 'llm' ? true : freeText.trim().length > 0

  return (
    <div className="light-card p-4" data-testid="probe-panel" data-tour="probe-panel">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-iflow-900 mb-2">
        <Compass className="w-4 h-4" /> Probe this region on the map
        <HelpPopover topic="ws-probes" size={13} />
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Test a variation of your idea against the corpus — it is embedded with the same model and placed on the map
        so you can compare it against the ranked regions. Looking for a full write-up instead?{' '}
        {onRunNoveltyAnalysis ? (
          <button type="button" onClick={onRunNoveltyAnalysis} className="text-iflow-600 hover:text-iflow-700 underline">
            Run novelty analysis
          </button>
        ) : (
          'Run novelty analysis from the AI Analysis tab.'
        )}
        <HelpPopover topic="ws-novelty-handoff" size={12} label="Help: what running a novelty analysis does" />
      </p>

      {initialContext && (
        <p className="text-[10.5px] text-purple-700 bg-purple-50 border border-purple-100 rounded-lg px-2 py-1 mb-3" data-testid="probe-context-banner">
          Probing from: {initialContext}
        </p>
      )}

      <div className="flex gap-0.5 bg-sky-500/10 rounded-lg p-0.5 mb-3 w-fit" role="tablist" aria-label="Probe mode">
        <button
          role="tab"
          aria-selected={mode === 'llm'}
          onClick={() => setMode('llm')}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            mode === 'llm' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Suggest directions
        </button>
        <button
          role="tab"
          aria-selected={mode === 'freetext'}
          onClick={() => setMode('freetext')}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
            mode === 'freetext' ? 'bg-white text-iflow-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Describe your own
        </button>
      </div>

      {mode === 'llm' ? (
        <input
          type="text"
          value={contextHint}
          onChange={(e) => setContextHint(e.target.value)}
          placeholder="Optional: a hint about the direction to explore"
          className="w-full text-xs px-3 py-2 border border-sky-200 rounded-lg mb-3"
          aria-label="Probe direction hint"
        />
      ) : (
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Describe a variation in a sentence…"
          rows={2}
          className="w-full text-xs px-3 py-2 border border-sky-200 rounded-lg mb-3 resize-none"
          aria-label="Free-text probe description"
        />
      )}

      <button
        onClick={runProbe}
        disabled={!canRun || isRunning}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-md transition-colors disabled:opacity-50"
      >
        {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Compass className="w-3.5 h-3.5" />}
        {isRunning ? 'Placing on the map…' : mode === 'llm' ? 'Generate directions' : 'Place this probe on the map'}
      </button>

      {error && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
          {error}
        </div>
      )}
      {acceptError && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
          {acceptError}
        </div>
      )}

      {lastProbes.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="probe-results">
          {lastProbes.map((p, i) => {
            const receipt = acceptReceipts[p.text]
            const display = probeLandingDisplay(p, receipt)
            return (
              <li key={i} className="flex items-start justify-between gap-2 p-2 bg-purple-50 border border-purple-100 rounded-lg">
                <div className="min-w-0">
                  <p className="text-[11.5px] text-gray-800 leading-snug">{p.text}</p>
                  {p.error ? (
                    <span className="text-[10px] text-red-600">Could not land this probe ({p.error}).</span>
                  ) : (
                    <div className="mt-0.5 space-y-0.5">
                      {display.distanceLabel && <p className="text-[10px] text-gray-500">{display.distanceLabel}</p>}
                      {display.anchorLabel && <p className="text-[10px] text-gray-500">{display.anchorLabel}</p>}
                      {display.noNearbyLabel && <p className="text-[10px] text-gray-500">{display.noNearbyLabel}</p>}
                      {display.plausibilityFlag && (
                        <p className="text-[10px] text-amber-700 flex items-center gap-1" data-testid="probe-plausibility-flag">
                          <Zap className="w-3 h-3" /> {display.plausibilityFlag}
                        </p>
                      )}
                      {display.receiptLabel && (
                        <p className="text-[10px] text-green-700 flex items-center gap-1" data-testid="probe-accept-receipt">
                          <CheckCircle2 className="w-3 h-3" /> {display.receiptLabel}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                {!p.error && (
                  <button
                    onClick={() => acceptProbe(p)}
                    disabled={acceptedTexts.has(p.text) || acceptingText === p.text}
                    className="flex-shrink-0 px-2 py-1 text-[10.5px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors disabled:opacity-50"
                  >
                    {acceptingText === p.text ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : acceptedTexts.has(p.text) ? (
                      'Accepted'
                    ) : (
                      'Accept → What Next'
                    )}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
